/**
 * executeStepStore.ts
 *
 * Zustand store for the Execute Step Runner UI state.
 * Manages: current step index, flattened steps, sidebar panel,
 * dirty checkpoint values, and step stats.
 */

import { create } from 'zustand';
import type {
  RunStageStep,
  FlatExecuteStep,
  ExecuteEntity,
  PlanEntity,
  ExecuteCheckpointResponse,
} from '@/types/run-stage.types';
import {
  flattenExecuteSteps,
  findActiveStepIndex,
  computeStepStats,
  type StepStats,
} from '@/run-stage/utils/stepFlattener';

export type SidebarPanel = 'uploads' | 'comments' | 'approval' | 'notifications' | null;

interface ExecuteStepStoreState {
  /** Flattened step list for navigation */
  flatSteps: FlatExecuteStep[];
  /** Current step index in flatSteps */
  currentIndex: number;
  /** Step stats */
  stats: StepStats;
  /** Active sidebar panel */
  activeSidebarPanel: SidebarPanel;
  /** Dirty checkpoint responses (unsaved) keyed by checkpointId */
  dirtyCheckpoints: Map<string, ExecuteCheckpointResponse>;
  /** Checkpoint validation errors keyed by checkpointId */
  checkpointErrors: Map<string, string>;
  /** Whether we're saving */
  isSaving: boolean;
  /** Error message */
  error: string | null;
  /** Parameter diffs from v2 GET response */
  parameterDiffs: unknown[];

  // ── Actions ──
  /** Initialize from execute + plan data */
  init: (execute: ExecuteEntity, plan: PlanEntity | null) => void;
  /** Initialize from v2 GET response (pre-merged steps + diffs) */
  initFromV2: (steps: FlatExecuteStep[], parameterDiffs: unknown[]) => void;
  /** Navigate to step by index */
  goToStep: (index: number) => void;
  /** Navigate previous */
  prevStep: () => void;
  /** Navigate next */
  nextStep: () => void;
  /** Jump to active step */
  goToActiveStep: () => void;
  /** Toggle sidebar panel */
  toggleSidebarPanel: (panel: SidebarPanel) => void;
  /** Update a checkpoint value (dirty) */
  setCheckpointValue: (checkpointId: string, value: unknown) => void;
  /** Clear dirty checkpoints */
  clearDirtyCheckpoints: () => void;
  /** Set saving state */
  setSaving: (saving: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** Get current flat step */
  getCurrentStep: () => FlatExecuteStep | undefined;
}

export const useExecuteStepStore = create<ExecuteStepStoreState>((set, get) => ({
  flatSteps: [],
  currentIndex: 0,
  stats: { total: 0, completed: 0, pending: 0, skipped: 0, repeated: 0, inProgress: 0 },
  activeSidebarPanel: null,
  dirtyCheckpoints: new Map(),
  checkpointErrors: new Map(),
  isSaving: false,
  error: null,
  parameterDiffs: [],

  init: (execute, plan) => {
    const flatSteps = flattenExecuteSteps(execute.steps ?? [], plan);
    const activeIndex = findActiveStepIndex(flatSteps);
    const stats = computeStepStats(execute.steps ?? []);
    set({
      flatSteps,
      currentIndex: activeIndex,
      stats,
      parameterDiffs: [],
      dirtyCheckpoints: new Map(),
      checkpointErrors: new Map(),
      error: null,
    });
  },

  initFromV2: (steps, parameterDiffs) => {
    const activeIndex = findActiveStepIndex(steps);
    const statusCounts = { total: steps.length, completed: 0, pending: 0, skipped: 0, repeated: 0, inProgress: 0 };
    for (const s of steps) {
      const status = s.step?.status?.toUpperCase?.() ?? '';
      if (status === 'COMPLETED') statusCounts.completed++;
      else if (status === 'SKIPPED') statusCounts.skipped++;
      else if (status === 'IN_PROGRESS') statusCounts.inProgress++;
      else statusCounts.pending++;
      if (s.step?.isRepeated) statusCounts.repeated++;
    }
    set({
      flatSteps: steps,
      currentIndex: activeIndex,
      stats: statusCounts,
      parameterDiffs,
      dirtyCheckpoints: new Map(),
      checkpointErrors: new Map(),
      error: null,
    });
  },

  goToStep: (index) => {
    const { flatSteps } = get();
    if (index >= 0 && index < flatSteps.length) {
      set({ currentIndex: index, dirtyCheckpoints: new Map(), checkpointErrors: new Map() });
    }
  },

  prevStep: () => {
    const { currentIndex } = get();
    if (currentIndex > 0) {
      set({ currentIndex: currentIndex - 1, dirtyCheckpoints: new Map(), checkpointErrors: new Map() });
    }
  },

  nextStep: () => {
    const { currentIndex, flatSteps } = get();
    if (currentIndex < flatSteps.length - 1) {
      set({ currentIndex: currentIndex + 1, dirtyCheckpoints: new Map(), checkpointErrors: new Map() });
    }
  },

  goToActiveStep: () => {
    const { flatSteps } = get();
    const activeIndex = findActiveStepIndex(flatSteps);
    set({ currentIndex: activeIndex, dirtyCheckpoints: new Map(), checkpointErrors: new Map() });
  },

  toggleSidebarPanel: (panel) => {
    const { activeSidebarPanel } = get();
    set({ activeSidebarPanel: activeSidebarPanel === panel ? null : panel });
  },

  setCheckpointValue: (checkpointId, value) => {
    const { dirtyCheckpoints } = get();
    const next = new Map(dirtyCheckpoints);
    const existing = next.get(checkpointId);
    next.set(checkpointId, {
      checkpointId,
      ...existing,
      value,
    });
    set({ dirtyCheckpoints: next });
  },

  clearDirtyCheckpoints: () => {
    set({ dirtyCheckpoints: new Map(), checkpointErrors: new Map() });
  },

  setSaving: (saving) => set({ isSaving: saving }),
  setError: (error) => set({ error }),

  getCurrentStep: () => {
    const { flatSteps, currentIndex } = get();
    return flatSteps[currentIndex];
  },
}));
